/**
 * /fiches/eleve/premiere-circulation-a2 — Landing page ÉLÈVE
 * Destination du QR code partagé par le moniteur pour la 1re circulation.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Download, Printer, Bookmark, BookmarkCheck,
  Share2, CheckCircle2, Clock, MapPin, Route, Sparkles, ChevronRight,
} from 'lucide-react';

const RESOURCE_KEY = 'code2roues:eleve:ressources';
const RESOURCE_ID = 'premiere-circulation-a2';

const FICHE_META = {
  id: RESOURCE_ID,
  title: '1ʳᵉ circulation moto — A2',
  subtitle: 'Ton passage du plateau à la route',
  duration: '~2 h de leçon',
  category: 'Moto A2 · Circulation',
  ficheUrl: '/fiches/moto/premiere-circulation-a2',
  source: 'REMC · Sécurité Routière',
};

function getSavedResources() {
  try { return JSON.parse(localStorage.getItem(RESOURCE_KEY) || '[]'); }
  catch (_) { return []; }
}
function saveResource(meta) {
  const list = getSavedResources();
  if (list.find((r) => r.id === meta.id)) return list;
  const next = [...list, { ...meta, savedAt: new Date().toISOString() }];
  localStorage.setItem(RESOURCE_KEY, JSON.stringify(next));
  return next;
}
function removeResource(id) {
  const next = getSavedResources().filter((r) => r.id !== id);
  localStorage.setItem(RESOURCE_KEY, JSON.stringify(next));
  return next;
}

export default function PremiereCirculationEleveLanding() {
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [showToast, setShowToast] = useState(null);

  useEffect(() => {
    document.title = 'Ma 1ʳᵉ circulation A2 · code2roues';
    setIsSaved(!!getSavedResources().find((r) => r.id === RESOURCE_ID));
  }, []);

  const toast = (msg) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(null), 2500);
  };

  const handleSave = () => {
    if (isSaved) {
      removeResource(RESOURCE_ID);
      setIsSaved(false);
      toast('Fiche retirée de mes ressources');
    } else {
      saveResource(FICHE_META);
      setIsSaved(true);
      toast('✅ Fiche enregistrée dans mes ressources');
    }
  };

  const handleDownloadPDF = () => {
    const w = window.open(FICHE_META.ficheUrl, '_blank');
    if (w) {
      w.addEventListener('load', () => {
        setTimeout(() => w.print(), 800);
      });
    }
  };

  const handlePrint = () => {
    window.open(FICHE_META.ficheUrl + '?autoprint=1', '_blank');
  };

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      try {
        await navigator.share({ title: FICHE_META.title, text: 'Ma 1ʳᵉ circulation moto A2 — code2roues', url });
      } catch (_) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast('🔗 Lien copié dans le presse-papier');
      } catch (_) {
        toast('Copie du lien impossible');
      }
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-neutral-950/90 backdrop-blur border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[13px] font-semibold"
            data-testid="btn-back"
          >
            <ArrowLeft size={14} /> Retour
          </button>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-sky-400">code2roues.fr</div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-sky-600/25 via-sky-500/10 to-transparent p-6">
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-sky-600 text-white px-2 py-1 rounded-full">
              <Sparkles size={10} /> Ta fiche
            </span>
          </div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-sky-400 mb-2">
            <Route size={11} /> 1ʳᵉ leçon route
          </div>
          <h1 className="text-[24px] sm:text-[28px] font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {FICHE_META.title}
          </h1>
          <p className="text-white/70 text-[13px] mt-2 leading-relaxed">
            Cette fiche accompagne ton <strong className="text-white">passage du plateau à la route</strong>. Elle regroupe les <strong className="text-white">fondamentaux de la circulation moto</strong> : regard, positionnement, trajectoires, contrôles visuels et intersections simples.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-4 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-full">
              <Clock size={11} /> {FICHE_META.duration}
            </span>
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-full">
              <MapPin size={11} /> {FICHE_META.category}
            </span>
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-full">
              <CheckCircle2 size={11} /> Référentiel REMC
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="max-w-2xl mx-auto px-4 mt-5">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/40 mb-2">Que veux-tu faire ?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <ActionCard
            icon={BookOpen}
            title="Consulter la fiche complète"
            desc="Ouvre la fiche avec les 5 étapes et les 6 clés visuelles."
            onClick={() => window.open(FICHE_META.ficheUrl, '_blank')}
            variant="primary"
            testId="action-consulter"
          />
          <ActionCard
            icon={isSaved ? BookmarkCheck : Bookmark}
            title={isSaved ? 'Enregistrée ✓' : 'Sauvegarder dans mes ressources'}
            desc={isSaved ? 'Retrouve-la à tout moment sur cet appareil.' : 'Garde-la accessible même hors connexion.'}
            onClick={handleSave}
            variant={isSaved ? 'success' : 'default'}
            testId="action-sauvegarder"
          />
          <ActionCard
            icon={Download}
            title="Télécharger en PDF"
            desc="Ouvre l'impression → « Enregistrer au format PDF »."
            onClick={handleDownloadPDF}
            variant="default"
            testId="action-telecharger"
          />
          <ActionCard
            icon={Printer}
            title="Imprimer"
            desc="Format A4, prêt pour ton classeur de formation."
            onClick={handlePrint}
            variant="default"
            testId="action-imprimer"
          />
        </div>

        <button
          onClick={handleShare}
          className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/80 text-[13px] font-semibold transition"
          data-testid="action-partager"
        >
          <Share2 size={14} /> Partager la fiche avec un(e) camarade
        </button>
      </div>

      {/* Aperçu des 6 clés */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/40 mb-2">Aperçu · les 6 clés de la 1ʳᵉ circulation</div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
          {CLES.map((k, i) => (
            <CleRow key={i} n={i + 1} title={k.title} note={k.note} color={k.color} last={i === CLES.length - 1} />
          ))}
        </div>
      </div>

      {/* Pourquoi c'est important */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">💡 Pourquoi c&apos;est important</div>
          <p className="text-white/80 text-[13px] leading-relaxed">
            En moto, <strong>85 % des accidents</strong> ont lieu en circulation — pas sur plateau. Cette 1<sup>ʳᵉ</sup> leçon route est le moment où tu construis les <strong>automatismes</strong> qui te protégeront toute ta vie de motard : le <strong>regard haut</strong>, la <strong>trajectoire de sécurité</strong>, les <strong>3 contrôles visuels</strong> et la <strong>distance de sécurité</strong>. Relis cette fiche avant chaque leçon.
          </p>
          <div className="text-[10.5px] text-white/45 mt-2 italic">
            Source pédagogique : {FICHE_META.source} · Référentiel Compétences C2.
          </div>
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white text-neutral-900 px-4 py-2.5 rounded-full shadow-2xl text-[13px] font-semibold" data-testid="toast">
          {showToast}
        </div>
      )}
    </div>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────

function ActionCard({ icon: Icon, title, desc, onClick, variant = 'default', testId }) {
  const styles = {
    primary: 'border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-white',
    success: 'border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-white',
    default: 'border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white',
  }[variant];

  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition group ${styles}`}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {title}
          </div>
          <div className="text-[11.5px] text-white/60 mt-1 leading-snug">{desc}</div>
        </div>
      </div>
    </button>
  );
}

function CleRow({ n, title, note, color, last }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${last ? '' : 'border-b border-white/5'}`}>
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-[12px] font-black shrink-0"
        style={{ background: color }}
      >
        {n}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-white text-[13px] font-bold leading-tight">{title}</div>
        <div className="text-white/50 text-[11px] mt-0.5">{note}</div>
      </div>
      <ChevronRight size={14} className="text-white/30" />
    </div>
  );
}

const CLES = [
  { title: 'Le regard : haut, loin, mobile', note: 'La moto va où le regard va — anticipe 10-15 s', color: '#0284c7' },
  { title: 'Positionnement sur la voie', note: 'Tiers droit · éviter huile & gravillons', color: '#0891b2' },
  { title: 'Trajectoire de sécurité', note: 'Extérieur → Intérieur → Extérieur', color: '#059669' },
  { title: 'Les 3 contrôles visuels', note: 'Rétro → Épaule → Clignotant → Action', color: '#d97706' },
  { title: 'Distance de sécurité', note: '2 sec (sec) · 4 sec (pluie) — « 1 tulipe, 2 tulipes »', color: '#dc2626' },
  { title: 'Intersections simples', note: 'STOP · Cédez · Priorité à droite', color: '#7c3aed' },
];
